export type HelpItem = {
  title: string
  content: string | string[] // string for paragraphs, array for lists
}

export type HelpSection = {
  id: string
  title: string
  items: HelpItem[]
}

export const HELP_CONTENT: Record<'en' | 'pt', HelpSection[]> = {
  en: [
    {
      id: 'getting-started',
      title: '1. Getting Started',
      items: [
        {
          title: 'Overview',
          content:
            'Trail Race Planner helps athletes plan ultra trail races by estimating total time, segment pacing, nutrition, hydration, and checkpoints based on GPX data and athlete profile. All calculations are estimates intended to support decision-making, not exact predictions.',
        },
        {
          title: 'Typical Workflow',
          content: [
            '1. Import a GPX file of your route',
            '2. Configure your Athlete Profile',
            '3. Define or review segments (checkpoints)',
            '4. Select a Pacing Model',
            '5. Review pacing, checkpoints, and nutrition',
            '6. Export or review your race plan',
          ],
        },
      ],
    },
    {
      id: 'core-concepts',
      title: '2. Core Concepts',
      items: [
        {
          title: 'GPX & Segments',
          content:
            'A segment is the section of the course between two checkpoints. Distance and elevation are calculated automatically from the GPX data. Segments are the base unit for all pacing and nutrition calculations.',
        },
        {
          title: 'Athlete Profile',
          content:
            'Your personal data (experience level, flat speed, climbing ability, heat tolerance, etc.) directly influences the pacing algorithm and nutrition recommendations.',
        },
        {
          title: 'Pacing Models',
          content:
            'Pacing models determine how your speed changes over the course. They account for flat pace, elevation gain/loss, terrain type, and fatigue multipliers per segment.',
        },
        {
          title: 'Checkpoints',
          content:
            'Checkpoints (Aid Stations, Water Points, Base Camps) are where you stop. The app calculates ETA, cumulative time, and nutrition status for each.',
        },
        {
          title: 'Nutrition Logic',
          content:
            'Hourly targets (carbs, calories, sodium, fluids) are translated into per-segment recommendations based on the estimated time to complete that segment.',
        },
      ],
    },
    {
      id: 'features',
      title: '3. Feature Guides',
      items: [
        {
          title: 'GPX Import',
          content:
            'Upload a standard .gpx file. The app analyzes the track to extract distance, elevation profile, and coordinates.',
        },
        {
          title: 'Athlete Profile',
          content:
            'Configure your "Flat Effort" (sustainable pace) and "Fatigue Onset" (when you start slowing down). These are critical for accurate time predictions.',
        },
        {
          title: 'Pacing Table',
          content:
            'Shows a row for each segment. You can see distance, elevation, estimated time, and ETA. Click a row to adjust "Effort Pace" or "Stop Time" manually.',
        },
        {
          title: 'Nutrition Planning',
          content:
            'Define your hourly targets in Settings. Then, in the Nutrition tab, assign products from your library to meet those targets for each segment.',
        },
      ],
    },
    {
      id: 'faq',
      title: '4. FAQ',
      items: [
        {
          title: 'Why does my time change when I edit the profile?',
          content:
            'The pacing algorithm uses your profile (speed, fatigue resistance) as the baseline variables for the calculation.',
        },
        {
          title: 'How accurate are the predictions?',
          content:
            'They are mathematical estimates based on your inputs. Weather, injury, and race-day conditions will cause variations.',
        },
        {
          title: 'Can I use this during the race?',
          content:
            'This tool is primarily for pre-race planning. You can export the plan to PDF or JSON for reference, but it does not track live GPS.',
        },
        {
          title: 'What happens if I change a nutrition target?',
          content:
            'All segment recommendations will automatically recalculate to match the new hourly target.',
        },
      ],
    },
    {
      id: 'tips',
      title: '5. Practical Tips',
      items: [
        {
          title: 'Quick Draft',
          content:
            'Import GPX → Run Calibration Wizard → Check Pacing Table. You can have a rough plan in under 2 minutes.',
        },
        {
          title: 'Hot Races',
          content:
            'If the race is hot, increase your Fluid and Sodium targets in Settings. The app will then recommend carrying more water/salt.',
        },
        {
          title: 'Checkpoints',
          content:
            'Use the "Stop Time" field in the Pacing Table to account for rest, gear changes, or hot food at aid stations.',
        },
      ],
    },
    {
      id: 'limitations',
      title: '6. Limitations',
      items: [
        {
          title: 'Assumptions',
          content:
            'Calculations assume steady effort relative to terrain. It does not account for acute injuries or mechanical failures.',
        },
        {
          title: 'Weather',
          content:
            'While we fetch a forecast, the pacing model does not automatically slow you down for rain/mud unless you manually adjust the terrain type or execution scenario.',
        },
      ],
    },
    {
      id: 'glossary',
      title: '7. Glossary',
      items: [
        {
          title: 'Segment',
          content: 'The portion of the trail between two checkpoints.',
        },
        {
          title: 'ETA',
          content: 'Estimated Time of Arrival (time of day).',
        },
        {
          title: 'D+ / D-',
          content: 'Elevation Gain (Ascent) and Elevation Loss (Descent).',
        },
        {
          title: 'Base of Life (BV)',
          content:
            'A major aid station where you have access to a drop bag with spare gear/food.',
        },
      ],
    },
  ],
  pt: [
    {
      id: 'getting-started',
      title: '1. Começar',
      items: [
        {
          title: 'Visão Geral',
          content:
            'O Trail Race Planner ajuda atletas a planear ultra trails estimando tempo total, ritmo por segmento, nutrição, hidratação e postos de controlo baseados em dados GPX e perfil do atleta. Todos os cálculos são estimativas para apoio à decisão.',
        },
        {
          title: 'Fluxo Típico',
          content: [
            '1. Importar ficheiro GPX do percurso',
            '2. Configurar Perfil de Atleta',
            '3. Definir ou rever segmentos (postos)',
            '4. Selecionar Modelo de Ritmo',
            '5. Rever ritmo, postos e nutrição',
            '6. Exportar ou rever o plano de corrida',
          ],
        },
      ],
    },
    {
      id: 'core-concepts',
      title: '2. Conceitos Base',
      items: [
        {
          title: 'GPX e Segmentos',
          content:
            'Um segmento é a secção do percurso entre dois postos. Distância e elevação são calculadas automaticamente do GPX. Segmentos são a unidade base para cálculos de ritmo e nutrição.',
        },
        {
          title: 'Perfil de Atleta',
          content:
            'Os teus dados pessoais (nível de experiência, velocidade em plano, capacidade de subida, tolerância ao calor, etc.) influenciam diretamente o algoritmo de ritmo e recomendações nutricionais.',
        },
        {
          title: 'Modelos de Ritmo',
          content:
            'Determinam como a tua velocidade muda ao longo do curso. Consideram ritmo em plano, desnível, tipo de terreno e multiplicadores de fadiga por segmento.',
        },
        {
          title: 'Postos de Controlo',
          content:
            'Postos (Abastecimentos, Pontos de Água, Bases de Vida) são onde paras. A app calcula ETA, tempo acumulado e estado nutricional para cada um.',
        },
        {
          title: 'Lógica de Nutrição',
          content:
            'Objetivos horários (hidratos, calorias, sódio, líquidos) são traduzidos em recomendações por segmento baseadas no tempo estimado para completar esse segmento.',
        },
      ],
    },
    {
      id: 'features',
      title: '3. Funcionalidades',
      items: [
        {
          title: 'Importação GPX',
          content:
            'Carrega um ficheiro .gpx padrão. A app analisa o trilho para extrair distância, perfil de elevação e coordenadas.',
        },
        {
          title: 'Perfil de Atleta',
          content:
            'Configura o teu "Esforço Plano" (ritmo sustentável) e "Início de Fadiga". Estes são críticos para previsões de tempo precisas.',
        },
        {
          title: 'Tabela de Ritmo',
          content:
            'Mostra uma linha por segmento. Podes ver distância, elevação, tempo estimado e ETA. Clica numa linha para ajustar "Ritmo de Esforço" ou "Tempo de Paragem" manualmente.',
        },
        {
          title: 'Planeamento Nutricional',
          content:
            'Define os teus objetivos horários nas Definições. Depois, no separador Nutrição, atribui produtos da tua biblioteca para cumprir esses objetivos em cada segmento.',
        },
      ],
    },
    {
      id: 'faq',
      title: '4. FAQ',
      items: [
        {
          title: 'Porque muda o tempo quando edito o perfil?',
          content:
            'O algoritmo de ritmo usa o teu perfil (velocidade, resistência à fadiga) como variáveis base para o cálculo.',
        },
        {
          title: 'Quão precisas são as previsões?',
          content:
            'São estimativas matemáticas baseadas nos teus dados. Meteorologia, lesões e condições do dia da corrida causarão variações.',
        },
        {
          title: 'Posso usar durante a corrida?',
          content:
            'Esta ferramenta é primariamente para planeamento pré-corrida. Podes exportar o plano para PDF ou JSON para referência, mas não faz seguimento GPS em tempo real.',
        },
        {
          title: 'O que acontece se mudar um objetivo nutricional?',
          content:
            'Todas as recomendações por segmento serão recalculadas automaticamente para corresponder ao novo objetivo horário.',
        },
      ],
    },
    {
      id: 'tips',
      title: '5. Dicas Práticas',
      items: [
        {
          title: 'Rascunho Rápido',
          content:
            'Importar GPX → Assistente de Calibração → Verificar Tabela de Ritmo. Podes ter um plano base em menos de 2 minutos.',
        },
        {
          title: 'Corridas Quentes',
          content:
            'Se a corrida for quente, aumenta os objetivos de Líquidos e Sódio nas Definições. A app recomendará levar mais água/sal.',
        },
        {
          title: 'Postos de Controlo',
          content:
            'Usa o campo "Tempo de Paragem" na Tabela de Ritmo para contabilizar descanso, trocas de roupa ou comida quente nos abastecimentos.',
        },
      ],
    },
    {
      id: 'limitations',
      title: '6. Limitações',
      items: [
        {
          title: 'Pressupostos',
          content:
            'Os cálculos assumem esforço constante relativo ao terreno. Não contabiliza lesões agudas ou falhas mecânicas.',
        },
        {
          title: 'Meteorologia',
          content:
            'Embora mostremos a previsão, o modelo de ritmo não te abranda automaticamente por chuva/lama a menos que ajustes manualmente o tipo de terreno ou cenário de execução.',
        },
      ],
    },
    {
      id: 'glossary',
      title: '7. Glossário',
      items: [
        {
          title: 'Segmento',
          content: 'A porção do trilho entre dois postos de controlo.',
        },
        {
          title: 'ETA',
          content: 'Hora Estimada de Chegada (Estimated Time of Arrival).',
        },
        {
          title: 'D+ / D-',
          content: 'Desnível Positivo (Subida) e Desnível Negativo (Descida).',
        },
        {
          title: 'Base de Vida (BV)',
          content:
            'Um abastecimento principal onde tens acesso a um saco com roupa/comida extra.',
        },
      ],
    },
  ],
}
